import React, { useMemo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Section from '@components/section';
import Wrapper from '@components/wrapper.styled';
import Text from '@components/text';
import AnimatedElement from '@components/animated-element';
import {
  StyledHeading,
  Divider,
  StyledGrid,
  StyledBlock,
  StyledIcon,
  StyledTextContent,
  Container,
  TextContent,
  StyledIconImage,
  StyledSocialLinks,
  StyledSocialLink,
  StyledSocialIconImage,
} from './content-columns.styled';

interface ContentColumnsItemType {
  id?: string;
  image?: ImageProps;
  title?: {
    text: string;
  };
  description?: {
    richText: RichTextBlock[];
  };
  twitter_url?: {
    uid: string;
    url: string;
    target: string;
  };
  linkedin_url?: {
    uid: string;
    url: string;
    target: string;
  };
  facebook_url?: {
    uid: string;
    url: string;
    target: string;
  };
}

export interface ContentColumnsProps {
  twitter_icon?: ImageProps;
  linkedin_icon?: ImageProps;
  facebook_icon?: ImageProps;
  title?: string;
  description?: RichTextBlock[];
  items?: ContentColumnsItemType[];
}

const ContentColumns: React.FC<ContentColumnsProps> = ({
  title,
  description,
  items = [],
  twitter_icon,
  linkedin_icon,
  facebook_icon,
}) => {
  const hasItems = useMemo(() => {
    if (!items.length) return false;
    const firstItem = items[0];
    return Boolean(
      firstItem?.image?.url || firstItem?.description?.richText?.length || firstItem?.title?.text
    );
  }, [items]);

  const renderGrid = useMemo(() => {
    if (!items.length) return null;

    return (
      <StyledGrid role="list" aria-label="Feature list">
        {items.map((item) => (
          <StyledBlock role="listitem" key={item.id || item.title?.text || `item-${Math.random()}`}>
            <StyledIcon>
              {(item.image?.gatsbyImageData || item.image?.url) && (
                <StyledIconImage
                  gatsbyImageData={item.image?.gatsbyImageData}
                  url={item.image?.url}
                  alt={item.image?.alt || item.title?.text || 'Feature icon'}
                  objectFit="contain"
                  loading="lazy"
                  objectPosition="0% 50%"
                />
              )}
            </StyledIcon>
            <TextContent>
              {item.title?.text && (
                <Text
                  type="title"
                  tag="h3"
                  stringText={item.title.text}
                  fontSize="heading5"
                  fontColor="black"
                  margin="none"
                />
              )}
              {item.description?.richText && (
                <Text
                  type="description"
                  richText={item.description.richText}
                  fontSize="small"
                  fontColor="dark"
                  margin="none"
                />
              )}
            </TextContent>
            {(item.twitter_url || item.linkedin_url || item.facebook_url) && (
              <StyledSocialLinks>
                {twitter_icon && item.twitter_url?.url && twitter_icon.url && (
                  <StyledSocialLink
                    href={item.twitter_url?.url}
                    target={item.twitter_url?.target || '_blank'}
                    rel="noopener noreferrer"
                  >
                    <StyledSocialIconImage imageUrl={twitter_icon.url} />
                  </StyledSocialLink>
                )}
                {linkedin_icon && item.linkedin_url?.url && linkedin_icon.url && (
                  <StyledSocialLink
                    href={item.linkedin_url?.url}
                    target={item.linkedin_url?.target || '_blank'}
                    rel="noopener noreferrer"
                  >
                    <StyledSocialIconImage imageUrl={linkedin_icon.url} />
                  </StyledSocialLink>
                )}
                {facebook_icon && item.facebook_url?.url && facebook_icon.url && (
                  <StyledSocialLink
                    href={item.facebook_url?.url}
                    target={item.facebook_url?.target || '_blank'}
                    rel="noopener noreferrer"
                  >
                    <StyledSocialIconImage imageUrl={facebook_icon.url} />
                  </StyledSocialLink>
                )}
              </StyledSocialLinks>
            )}
          </StyledBlock>
        ))}
      </StyledGrid>
    );
  }, [items]);

  return (
    <Section backgroundColor="white">
      <Wrapper>
        <Container>
          <StyledTextContent>
            <AnimatedElement animationType="scaleX" delay={0.25} threshold={1} rootMargin="50px">
              <Divider />
            </AnimatedElement>
            {title && (
              <StyledHeading
                type="title"
                tag="h2"
                stringText={title}
                fontSize="heading2"
                fontColor="black"
                margin="none"
              />
            )}
            {description && description.length > 0 && description[0] && description[0].text && (
              <Text
                type="subheading"
                richText={description}
                fontSize="large"
                fontColor="dark"
                margin="none"
              />
            )}
          </StyledTextContent>
          {hasItems && renderGrid}
        </Container>
      </Wrapper>
    </Section>
  );
};

export default React.memo(ContentColumns);
